import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  usePortalPunchCards,
  usePortalPunchCardShop,
  usePurchasePunchCard,
  syncPunchCardCheckout,
  type PortalPunchCardItem,
  type PortalPunchCardUsage,
  type PunchCardStatus,
} from '@/hooks/usePortalPunchCards';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import type { PunchCardTemplate } from '@/hooks/usePunchCardTemplates';
import { PortalPageHead } from '@/components/portal/PortalChrome';
import { PortalIcon } from '@/components/portal/PortalIcon';
import { formatPortalDateTime, formatPortalKroner, formatPortalShortDate } from '@/lib/portalFormat';

const PORTAL_PUNCH_CARDS_PATH = '/portal/punch-cards';

export function PortalPunchCards() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activePatientId } = usePortalActiveClinic();
  const {
    data: punchCards = [],
    isLoading,
    error,
    refetch,
  } = usePortalPunchCards();
  const { data: shopTemplates = [] } = usePortalPunchCardShop();
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'used'>('all');

  const purchaseStatus = searchParams.get('purchase');
  const returnPayToken = searchParams.get('pay_token');

  useEffect(() => {
    if (purchaseStatus !== 'succeeded' && purchaseStatus !== 'cancelled') return;

    let cancelled = false;

    async function finishReturn() {
      if (purchaseStatus === 'succeeded') {
        if (returnPayToken) {
          try {
            await syncPunchCardCheckout(returnPayToken);
          } catch (err) {
            console.error('punch card purchase sync failed', err);
          }
        }
        if (!cancelled) {
          toast.success(t('portal.punch_cards.purchase_success'));
          void refetch();
        }
      } else if (!cancelled) {
        toast(t('portal.punch_cards.purchase_cancelled'));
      }
      if (!cancelled) {
        setSearchParams({}, { replace: true });
      }
    }

    void finishReturn();
    return () => {
      cancelled = true;
    };
  }, [purchaseStatus, returnPayToken, refetch, setSearchParams, t]);

  const filteredCards = useMemo(() => {
    if (activeTab === 'active') {
      return punchCards.filter((card) => card.status === 'active');
    }
    if (activeTab === 'used') {
      return punchCards.filter((card) => card.status === 'used' || card.status === 'expired');
    }
    return punchCards;
  }, [punchCards, activeTab]);

  const counts = useMemo(() => {
    const active = punchCards.filter((card) => card.status === 'active').length;
    const used = punchCards.filter((card) => card.status === 'used' || card.status === 'expired').length;
    return { all: punchCards.length, active, used };
  }, [punchCards]);

  return (
    <>
      <PortalPageHead title="Klippekort" subtitle={t('portal.punch_cards.subtitle')} />

      {shopTemplates.length > 0 && activePatientId ? (
        <ShopSection templates={shopTemplates} patientId={activePatientId} />
      ) : null}

      <div className="seg" role="group" aria-label="Filter" style={{ marginBottom: 16 }}>
        <button type="button" aria-pressed={activeTab === 'all'} onClick={() => setActiveTab('all')}>
          {t('portal.punch_cards.tab_all')} <span className="count">{counts.all}</span>
        </button>
        <button type="button" aria-pressed={activeTab === 'active'} onClick={() => setActiveTab('active')}>
          {t('portal.punch_cards.tab_active')} <span className="count">{counts.active}</span>
        </button>
        <button type="button" aria-pressed={activeTab === 'used'} onClick={() => setActiveTab('used')}>
          {t('portal.punch_cards.tab_used')}
        </button>
      </div>

      {isLoading ? (
        <section className="card">
          <p className="help">Henter klippekort…</p>
        </section>
      ) : error ? (
        <section className="card">
          <p className="help">{t('portal.punch_cards.error')}</p>
        </section>
      ) : filteredCards.length === 0 ? (
        <section className="card">
          <p className="help">
            {activeTab === 'all'
              ? shopTemplates.length > 0
                ? t('portal.punch_cards.empty_buy_subtitle')
                : t('portal.punch_cards.empty_subtitle')
              : activeTab === 'active'
                ? t('portal.punch_cards.empty_active_subtitle')
                : t('portal.punch_cards.empty_used_subtitle')}
          </p>
        </section>
      ) : (
        filteredCards.map((card) => <PunchCardCard key={card.id} card={card} />)
      )}
    </>
  );
}

function ShopSection({
  templates,
  patientId,
}: {
  templates: PunchCardTemplate[];
  patientId: string;
}) {
  const { t } = useTranslation();
  const purchase = usePurchasePunchCard();
  const [buyingId, setBuyingId] = useState<string | null>(null);

  const handleBuy = async (template: PunchCardTemplate) => {
    setBuyingId(template.id);
    try {
      const result = await purchase.mutateAsync({
        templateId: template.id,
        patientId,
        returnPath: PORTAL_PUNCH_CARDS_PATH,
      });
      window.location.href = result.checkout_url;
    } catch (err) {
      console.error('punch card purchase failed', err);
      toast.error(t('portal.punch_cards.purchase_error'));
      setBuyingId(null);
    }
  };

  return (
    <section className="card" style={{ marginBottom: 24 }}>
      <div className="card-h">
        <h2>{t('portal.punch_cards.shop_title')}</h2>
      </div>
      <p className="help">{t('portal.punch_cards.shop_subtitle')}</p>
      {templates.map((template) => {
        const isBuying = buyingId === template.id;
        return (
          <div key={template.id} className="row">
            <span className="ic-tile">
              <PortalIcon name="ticket" />
            </span>
            <div className="grow">
              <div className="t">{template.name}</div>
              <div className="s">
                {template.total_clips} {t('portal.punch_cards.clips')}
                {template.expires_months
                  ? ` · ${t('portal.punch_cards.valid_months', { count: template.expires_months })}`
                  : ''}
                {template.description ? ` · ${template.description}` : ''}
              </div>
            </div>
            <div className="r">
              <b className="num">{formatPortalKroner(template.price_cents)}</b>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void handleBuy(template)}
                disabled={purchase.isPending}
              >
                {isBuying ? t('portal.punch_cards.buy') + '…' : t('portal.punch_cards.buy')}
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
}

function PunchCardCard({ card }: { card: PortalPunchCardItem }) {
  const { t } = useTranslation();
  const remainingClips = card.total_clips - card.used_clips;
  const progressPercent = card.total_clips > 0 ? (remainingClips / card.total_clips) * 100 : 0;
  const usages = card.usages ?? [];
  const chip = statusChip(card.status, t);

  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <div className="row" style={{ borderTop: 0, paddingTop: 0 }}>
        <span className="ic-tile">
          <PortalIcon name="ticket" />
        </span>
        <div className="grow">
          <div className="t">{card.name}</div>
          <div className="s">
            {card.punch_card_number ? `#${card.punch_card_number} · ` : ''}
            {t('portal.punch_cards.purchased')} {formatPortalShortDate(card.purchased_at)}
            {card.expires_at ? ` · ${t('portal.punch_cards.expires')} ${formatPortalShortDate(card.expires_at)}` : ''}
          </div>
        </div>
        <span className={`chip chip-${chip.tone}`}>
          <PortalIcon name={chip.icon} size="xs" />
          {chip.label}
        </span>
      </div>
      <div className="clip-bar" aria-hidden="true">
        <i style={{ width: `${progressPercent}%` }} />
      </div>
      <p className="help" style={{ marginTop: 8 }}>
        {remainingClips} / {card.total_clips} {t('portal.punch_cards.clips')}
      </p>
      <div style={{ marginTop: 16 }}>
        <div className="card-h">
          <h2>{t('portal.punch_cards.history')}</h2>
        </div>
        {usages.length === 0 ? (
          <p className="help">{t('portal.punch_cards.no_history')}</p>
        ) : (
          usages.map((usage) => <UsageRow key={usage.id} usage={usage} />)
        )}
      </div>
    </section>
  );
}

function UsageRow({ usage }: { usage: PortalPunchCardUsage }) {
  const { t } = useTranslation();
  const label =
    usage.session_name || usage.appointment_type_name || t('portal.punch_cards.appointment');
  return (
    <div className="row">
      <span className="ic-tile">
        <PortalIcon name="calendar" />
      </span>
      <div className="grow">
        <div className="t">{label}</div>
        <div className="s">
          {formatPortalDateTime(usage.session_date ?? usage.used_at)}
          {usage.session_time ? ` · ${usage.session_time}` : ''}
        </div>
      </div>
      <span className="help">
        {t('portal.punch_cards.used_clips', { count: usage.clips_used })}
      </span>
    </div>
  );
}

function statusChip(
  status: PunchCardStatus,
  t: (key: string) => string,
): { tone: 'teal' | 'green' | 'amber' | 'neutral' | 'red'; icon: 'check' | 'clock' | 'x' | 'ticket'; label: string } {
  switch (status) {
    case 'active':
      return { tone: 'teal', icon: 'ticket', label: t('portal.punch_cards.status_active') };
    case 'used':
      return { tone: 'green', icon: 'check', label: t('portal.punch_cards.status_used') };
    case 'expired':
      return { tone: 'amber', icon: 'clock', label: t('portal.punch_cards.status_expired') };
    case 'cancelled':
      return { tone: 'neutral', icon: 'x', label: t('portal.punch_cards.status_cancelled') };
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}
